'use client'

/* ============================================================
   RR Design System — ThemeProvider
   The single source of truth for light / dark / system mode
   across every RR app. Mirrors next-themes' behavior:

   1. <ThemeScript /> renders a tiny inline <script> that runs
      synchronously, BEFORE React hydrates, so the correct
      `dark` class is on <html> on first paint (no flash).
   2. <ThemeProvider> wraps the app and exposes `useTheme()`
      to read/toggle theme from any client component.

   Usage in an app's root layout:

     import { ThemeProvider, ThemeScript } from '@rr/ui'

     <html lang="en" suppressHydrationWarning>
       <head>
         <ThemeScript />
       </head>
       <body>
         <ThemeProvider>
           {children}
         </ThemeProvider>
       </body>
     </html>
   ============================================================ */

import {
  createContext, useContext, useEffect, useState, type ReactNode,
} from 'react'
import { applyAccent, readStoredAccent } from './accent-color'

export type ThemePreference = 'light' | 'dark' | 'system'
export type ResolvedTheme = 'light' | 'dark'

interface ThemeContextValue {
  /** What the user picked: 'light' | 'dark' | 'system'. */
  theme: ThemePreference
  /** What's actually applied right now ('system' resolved). */
  resolvedTheme: ResolvedTheme
  /** Set a specific preference (persisted to localStorage). */
  setTheme: (t: ThemePreference) => void
  /** Flip between light & dark (ignores 'system'). */
  toggleTheme: () => void
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined)

const DEFAULT_STORAGE_KEY = 'rr-theme'

function getSystemTheme(): ResolvedTheme {
  if (typeof window === 'undefined') return 'light'
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
}

function applyTheme(resolved: ResolvedTheme) {
  const root = document.documentElement
  root.classList.toggle('dark', resolved === 'dark')
  root.style.colorScheme = resolved
}

/**
 * Re-apply a persisted brand accent (see accent-color.tsx) on first mount, so
 * a custom brand color survives reloads app-wide without the AppearanceTab
 * having to be visited.
 *
 * Delegates to accent-color.tsx rather than reading localStorage directly.
 * This used to inline `setProperty('--primary', <raw stored value>)`, which
 * wrote a legacy HSL triple straight onto the token after the tokens moved to
 * OKLCH — producing invalid `oklch(221 83% 53%)` and knocking out every
 * `bg-primary` surface. `readStoredAccent()` owns the format and its
 * migration, so there is exactly one place that knows what a stored accent
 * looks like.
 */
function applyStoredAccent() {
  applyAccent(readStoredAccent())
}

export interface ThemeProviderProps {
  children: ReactNode
  /** Default preference if nothing is in localStorage. */
  defaultTheme?: ThemePreference
  /** localStorage key. Override if you need per-app persistence. */
  storageKey?: string
}

export function ThemeProvider({
  children,
  defaultTheme = 'system',
  storageKey = DEFAULT_STORAGE_KEY,
}: ThemeProviderProps) {
  const [theme, setThemeState] = useState<ThemePreference>(defaultTheme)
  const [resolvedTheme, setResolvedTheme] = useState<ResolvedTheme>('light')

  // First mount: read what's already on <html> (set by ThemeScript)
  // and reconcile the React state with it.
  useEffect(() => {
    const stored =
      (localStorage.getItem(storageKey) as ThemePreference | null) ?? defaultTheme
    setThemeState(stored)
    const resolved = stored === 'system' ? getSystemTheme() : stored
    setResolvedTheme(resolved)
    applyTheme(resolved)
    applyStoredAccent()
  }, [defaultTheme, storageKey])

  // Track OS preference changes while in 'system' mode.
  useEffect(() => {
    if (theme !== 'system') return
    const mql = window.matchMedia('(prefers-color-scheme: dark)')
    const onChange = () => {
      const next: ResolvedTheme = mql.matches ? 'dark' : 'light'
      setResolvedTheme(next)
      applyTheme(next)
    }
    mql.addEventListener('change', onChange)
    return () => mql.removeEventListener('change', onChange)
  }, [theme])

  const setTheme = (t: ThemePreference) => {
    setThemeState(t)
    try { localStorage.setItem(storageKey, t) } catch { /* private mode */ }
    const resolved = t === 'system' ? getSystemTheme() : t
    setResolvedTheme(resolved)
    applyTheme(resolved)
  }

  const toggleTheme = () =>
    setTheme(resolvedTheme === 'dark' ? 'light' : 'dark')

  return (
    <ThemeContext.Provider value={{ theme, resolvedTheme, setTheme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  )
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext)
  if (!ctx) throw new Error('useTheme must be used within a <ThemeProvider />')
  return ctx
}
